import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Calendar, Clock, MapPin, Users, Video, Plus, Pencil, Trash2 } from "lucide-react";
import { format } from "date-fns";

interface Event {
  id: string;
  title: string;
  description: string;
  event_date: string;
  event_type: string;
  location: string;
  max_attendees: number;
}

interface LiveSession {
  id: string;
  title: string;
  description: string;
  start_time: string;
  end_time: string;
  session_type: string;
  max_attendees: number;
  meeting_link: string;
}

const CommunityCalendar = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<Event[]>([]);
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [openSessionDialog, setOpenSessionDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [editingSession, setEditingSession] = useState<LiveSession | null>(null);
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    event_date: "",
    event_type: "workshop",
    location: "",
    max_attendees: 50
  });
  const [sessionForm, setSessionForm] = useState({
    title: "",
    description: "",
    start_time: "",
    end_time: "",
    session_type: "webinar",
    max_attendees: 100,
    meeting_link: ""
  });

  useEffect(() => {
    loadCalendarData();
  }, []);

  const loadCalendarData = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate("/auth");
      return;
    }

    // Check if user is admin
    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .single();
    
    setIsAdmin(!!roleData);

    const [eventsData, sessionsData] = await Promise.all([
      supabase
        .from("events")
        .select("*")
        .gte("event_date", new Date().toISOString())
        .order("event_date", { ascending: true }),
      supabase
        .from("live_sessions")
        .select("*")
        .gte("start_time", new Date().toISOString())
        .eq("status", "scheduled")
        .order("start_time", { ascending: true })
    ]);

    if (eventsData.data) setEvents(eventsData.data);
    if (sessionsData.data) setSessions(sessionsData.data);
    
    setLoading(false);
  };

  const registerForEvent = async (eventId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { error } = await supabase
      .from("event_registrations")
      .insert({
        event_id: eventId,
        user_id: session.user.id
      });

    if (error) {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Registered!",
        description: "You've successfully registered for this event.",
      });
    }
  };

  const handleCreateEvent = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { error } = await supabase
      .from("events")
      .insert({
        ...eventForm,
        created_by: session.user.id
      });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Event created successfully",
      });
      setOpenEventDialog(false);
      setEventForm({
        title: "",
        description: "",
        event_date: "",
        event_type: "workshop",
        location: "",
        max_attendees: 50
      });
      loadCalendarData();
    }
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;

    const { error } = await supabase
      .from("events")
      .update(eventForm)
      .eq("id", editingEvent.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Event updated successfully",
      });
      setOpenEventDialog(false);
      setEditingEvent(null);
      loadCalendarData();
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    const { error } = await supabase
      .from("events")
      .delete()
      .eq("id", eventId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Event deleted successfully",
      });
      loadCalendarData();
    }
  };

  const handleCreateSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { error } = await supabase
      .from("live_sessions")
      .insert({
        ...sessionForm,
        host_id: session.user.id,
        duration_minutes: Math.round(
          (new Date(sessionForm.end_time).getTime() - new Date(sessionForm.start_time).getTime()) / 60000
        )
      });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Session created successfully",
      });
      setOpenSessionDialog(false);
      setSessionForm({
        title: "",
        description: "",
        start_time: "",
        end_time: "",
        session_type: "webinar",
        max_attendees: 100,
        meeting_link: ""
      });
      loadCalendarData();
    }
  };

  const handleUpdateSession = async () => {
    if (!editingSession) return;

    const { error } = await supabase
      .from("live_sessions")
      .update({
        ...sessionForm,
        duration_minutes: Math.round(
          (new Date(sessionForm.end_time).getTime() - new Date(sessionForm.start_time).getTime()) / 60000
        )
      })
      .eq("id", editingSession.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Session updated successfully",
      });
      setOpenSessionDialog(false);
      setEditingSession(null);
      loadCalendarData();
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    const { error } = await supabase
      .from("live_sessions")
      .delete()
      .eq("id", sessionId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Session deleted successfully",
      });
      loadCalendarData();
    }
  };

  const openEditEventDialog = (event: Event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      description: event.description || "",
      event_date: event.event_date,
      event_type: event.event_type,
      location: event.location || "",
      max_attendees: event.max_attendees || 50
    });
    setOpenEventDialog(true);
  };

  const openEditSessionDialog = (session: LiveSession) => {
    setEditingSession(session);
    setSessionForm({
      title: session.title,
      description: session.description || "",
      start_time: session.start_time,
      end_time: session.end_time,
      session_type: session.session_type,
      max_attendees: session.max_attendees || 100,
      meeting_link: session.meeting_link || ""
    });
    setOpenSessionDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Button
        variant="ghost"
        onClick={() => navigate("/dashboard")}
        className="mb-6"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Dashboard
      </Button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold">Community Calendar</h1>
        <p className="text-muted-foreground">All upcoming events and live sessions</p>
      </div>

      <div className="grid gap-6">
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Video className="h-6 w-6" />
              Live Sessions
            </h2>
            {isAdmin && (
              <Dialog open={openSessionDialog} onOpenChange={setOpenSessionDialog}>
                <DialogTrigger asChild>
                  <Button onClick={() => {
                    setEditingSession(null);
                    setSessionForm({
                      title: "",
                      description: "",
                      start_time: "",
                      end_time: "",
                      session_type: "webinar",
                      max_attendees: 100,
                      meeting_link: ""
                    });
                  }}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Session
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editingSession ? "Edit Session" : "Create Live Session"}</DialogTitle>
                    <DialogDescription>
                      {editingSession ? "Update the session details" : "Add a new live session to the calendar"}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="session-title">Title</Label>
                      <Input
                        id="session-title"
                        value={sessionForm.title}
                        onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="session-description">Description</Label>
                      <Textarea
                        id="session-description"
                        value={sessionForm.description}
                        onChange={(e) => setSessionForm({ ...sessionForm, description: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="session-start">Start Time</Label>
                        <Input
                          id="session-start"
                          type="datetime-local"
                          value={sessionForm.start_time}
                          onChange={(e) => setSessionForm({ ...sessionForm, start_time: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="session-end">End Time</Label>
                        <Input
                          id="session-end"
                          type="datetime-local"
                          value={sessionForm.end_time}
                          onChange={(e) => setSessionForm({ ...sessionForm, end_time: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="session-type">Type</Label>
                        <Input
                          id="session-type"
                          value={sessionForm.session_type}
                          onChange={(e) => setSessionForm({ ...sessionForm, session_type: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="session-max">Max Attendees</Label>
                        <Input
                          id="session-max"
                          type="number"
                          value={sessionForm.max_attendees}
                          onChange={(e) => setSessionForm({ ...sessionForm, max_attendees: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="session-link">Meeting Link</Label>
                      <Input
                        id="session-link"
                        value={sessionForm.meeting_link}
                        onChange={(e) => setSessionForm({ ...sessionForm, meeting_link: e.target.value })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={editingSession ? handleUpdateSession : handleCreateSession}>
                      {editingSession ? "Update" : "Create"} Session
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="grid gap-4">
            {sessions.map((session) => (
              <Card key={session.id}>
                <CardHeader>
                  <CardTitle>{session.title}</CardTitle>
                  <CardDescription>{session.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{format(new Date(session.start_time), "PPP 'at' p")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>{session.session_type} • Max {session.max_attendees} attendees</span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {session.meeting_link && (
                        <Button
                          size="sm"
                          onClick={() => window.open(session.meeting_link, "_blank")}
                        >
                          Join Session
                        </Button>
                      )}
                      {isAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditSessionDialog(session)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteSession(session.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {sessions.length === 0 && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-center text-muted-foreground">No upcoming live sessions</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Calendar className="h-6 w-6" />
              Community Events
            </h2>
            {isAdmin && (
              <Dialog open={openEventDialog} onOpenChange={setOpenEventDialog}>
                <DialogTrigger asChild>
                  <Button onClick={() => {
                    setEditingEvent(null);
                    setEventForm({
                      title: "",
                      description: "",
                      event_date: "",
                      event_type: "workshop",
                      location: "",
                      max_attendees: 50
                    });
                  }}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Event
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editingEvent ? "Edit Event" : "Create Event"}</DialogTitle>
                    <DialogDescription>
                      {editingEvent ? "Update the event details" : "Add a new event to the community calendar"}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="event-title">Title</Label>
                      <Input
                        id="event-title"
                        value={eventForm.title}
                        onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="event-description">Description</Label>
                      <Textarea
                        id="event-description"
                        value={eventForm.description}
                        onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="event-date">Date</Label>
                        <Input
                          id="event-date"
                          type="datetime-local"
                          value={eventForm.event_date}
                          onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="event-type">Type</Label>
                        <Input
                          id="event-type"
                          value={eventForm.event_type}
                          onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="event-location">Location</Label>
                        <Input
                          id="event-location"
                          value={eventForm.location}
                          onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="event-max">Max Attendees</Label>
                        <Input
                          id="event-max"
                          type="number"
                          value={eventForm.max_attendees}
                          onChange={(e) => setEventForm({ ...eventForm, max_attendees: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={editingEvent ? handleUpdateEvent : handleCreateEvent}>
                      {editingEvent ? "Update" : "Create"} Event
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="grid gap-4">
            {events.map((event) => (
              <Card key={event.id}>
                <CardHeader>
                  <CardTitle>{event.title}</CardTitle>
                  <CardDescription>{event.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{format(new Date(event.event_date), "PPP 'at' p")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{event.location || "Online"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>{event.event_type} • Max {event.max_attendees} attendees</span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        onClick={() => registerForEvent(event.id)}
                      >
                        Register
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditEventDialog(event)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteEvent(event.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {events.length === 0 && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-center text-muted-foreground">No upcoming events</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityCalendar;
